import { FC, useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { useForm } from '@tanstack/react-form';
import { Button, InlineLoading, InlineNotification } from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { formatResource } from 'utils/misc';
import routes from '~/routes';
import { PriceDeckForm } from '../price-deck-form/price-deck-form';
import { PriceDeckFormProvider } from '../price-deck-form/context';
import { savePriceDeckAtom } from '../price-deck-form/atoms';
import {
  INITIAL_FORM,
  type PriceDeckFormInstance,
  type PriceDeckFormMode,
  type PriceDeckFormValues,
} from '../price-deck-form/types';
import { priceDeckDetailToFormValues } from '../utils';
import { usePriceDeckDetailAtom } from '../show/atoms';

// ─────────────────────────────────────
// Edit mode wrapper — isolates the atom hook so it only runs when id exists
// ─────────────────────────────────────

const EditPriceDeckContent: FC<{ id: string }> = ({ id }) => {
  const resource = useAtomValue(usePriceDeckDetailAtom(id));
  const { loading, data } = formatResource(resource);
  const initialData = data?.data ?? null;

  if (loading || !initialData) {
    return <InlineLoading description="Loading price deck..." />;
  }

  const defaultValues = priceDeckDetailToFormValues(initialData);

  return (
    <PriceDeckFormWrapper
      mode="edit"
      deckId={id}
      defaultValues={defaultValues}
    />
  );
};

// ─────────────────────────────────────
// Shared form wrapper — sets up TanStack Form + context
// ─────────────────────────────────────

const PriceDeckFormWrapper: FC<{
  mode: PriceDeckFormMode;
  deckId: string | null;
  defaultValues: PriceDeckFormValues;
}> = ({ mode, deckId, defaultValues }) => {
  const history = useHistory();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const triggerSave = useSetAtom(savePriceDeckAtom);

  const form = useForm({ defaultValues }) as unknown as PriceDeckFormInstance;

  const handleCancel = useCallback(() => {
    if (mode === 'edit' && deckId) {
      history.push(routes.priceDeckDetailPath.replace(':id', deckId));
    } else {
      history.push(routes.priceDecksPath);
    }
  }, [history, mode, deckId]);

  const handleSave = useCallback(async () => {
    const v = form.state.values;
    if (!v.name.trim()) return;

    setSaving(true);
    setSaveError(null);
    try {
      await triggerSave({
        payload: { priceDeck: v },
        mode,
        deckId,
      });
      // Navigate away — component unmounts, no further state updates needed
      if (mode === 'edit' && deckId) {
        history.push(routes.priceDeckDetailPath.replace(':id', deckId));
      } else {
        history.push(routes.priceDecksPath);
      }
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save price deck'
      );
      setSaving(false);
    }
  }, [form, triggerSave, mode, deckId, history]);

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title={mode === 'edit' ? 'Edit Price Deck' : 'Add Price Deck'}
        actions={
          <>
            <Button kind="ghost" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button kind="primary" onClick={handleSave} disabled={saving}>
              Save
            </Button>
          </>
        }
      />
      {saveError && (
        <InlineNotification
          kind="error"
          title="Save failed"
          subtitle={saveError}
          onCloseButtonClick={() => setSaveError(null)}
        />
      )}
      <PriceDeckFormProvider form={form} mode={mode} deckId={deckId}>
        <PriceDeckForm />
      </PriceDeckFormProvider>
    </>
  );
};

// ─────────────────────────────────────
// Page entry point
// ─────────────────────────────────────

const PriceDeckFormPage: FC = () => {
  const { id } = useParams<{ id?: string }>();
  const mode: PriceDeckFormMode = id ? 'edit' : 'create';

  if (mode === 'edit' && id) {
    return <EditPriceDeckContent id={id} />;
  }

  return (
    <PriceDeckFormWrapper
      mode="create"
      deckId={null}
      defaultValues={INITIAL_FORM}
    />
  );
};

export { PriceDeckFormPage };
